/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import React, { useCallback } from 'react';
import { AutocompleteRenderOptionState } from '@mui/material/Autocomplete';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import useMediaQuery from '@mui/material/useMediaQuery';

import { SearchItemType, selectFuseTagAutocomplete } from '../searchSlice';
import { AudienceType } from '../../algorithm/types';
import { IconsForDocumentFlags } from '../../documentVersionFlags/documentVersionFlags_utils';
import { PopperAutocomplete } from '../../Autocomplete/PopperAutocomplete';
import { SearchIcon } from './SearchIcon';
import styles from './SearchInput.module.scss';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import { closeFeedback } from '../DocumentVersionFeedback/feedbackSlice';

type SearchInputProps = {
  audience: AudienceType;
  publishedOnly: boolean;
  isSearching: boolean;
  onEnter: (input: string, slug: string | undefined) => Promise<void>;
  redirectToChat?: () => void;
};
export function SearchInput({ isSearching, audience, onEnter, publishedOnly, redirectToChat }: SearchInputProps) {
  const [searchText, setSearchText] = React.useState('');
  // Control whether the autocomplete popup is open
  const [open, setOpen] = React.useState(false);
  // Fuse is our fuzzy-search system.  It gets the list of tags / document titles, and lets you
  // do fuse.search('hello') to find the closest fuzzy matches.
  const fuse = useAppSelector((state) => selectFuseTagAutocomplete(state));
  const isSmallScreen = useMediaQuery('(max-width: 1000px)');
  const [keyboardSelected, setKeyboardSelected] = React.useState(false);

  const dispatch = useAppDispatch();

  async function doSearch(text_?: string) {
    const text = text_ ?? searchText.trim();
    if (isSearching) return;
    if (!text) {
      if (redirectToChat) redirectToChat();
      return;
    }
    setOpen(false); // For autocomplete popup to close
    // on mobile, close the keyboard popup
    if (isSmallScreen) {
      setTimeout(() => {
        (document.activeElement as any)?.blur?.();
      }, 100);
    }
    try {
      const match = fuse.search(text);
      let slug: undefined | string = undefined;
      if (match[0]?.item?.isTitle && match[0].item.name === text) {
        slug = match[0].item.slug;
      }
      await onEnter(text, slug);
    } finally {
      setSearchText('');
    }
  }
  function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault();
    void doSearch();
  }

  const renderOption = useCallback(
    (
      props: {},
      option: {
        item: SearchItemType;
      },
      state: AutocompleteRenderOptionState
    ) => (
      <span className={`${option.item.isTitle ? styles.title : styles.trigger}`}>
        <IconsForDocumentFlags flags={option.item.flags} fontSize="small" style={{ marginTop: -2 }} />{' '}
        <span>{option.item.name}</span>
      </span>
    ),
    []
  );

  const filterOptions = (options: any, { inputValue }: any) => fuse.search(inputValue);

  return (
    <form onSubmit={handleSubmit} id="search-input-form" className={`${styles.form} search-input-form`}>
      <PopperAutocomplete
        options={[]}
        open={open}
        blurOnSelect={isSmallScreen}
        filterOptions={filterOptions}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        value={searchText || ''}
        isOptionEqualToValue={(option, value) => option === value}
        freeSolo
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.item.name || '')}
        onChange={(e, v) => {
          if (v && typeof v === 'object') void doSearch(v.item.name);
        }}
        onHighlightChange={(x, result, reason) => {
          /* Desired behaviour:
              - When highlighting an entry with the mouse, the entry is faintly highlighted.  When pressing enter, the highlighted option is ignored, and the currently-typed text is used.
              - When selecting an entry with the keyboard, the entry is more heavily highlighted.  When pressing enter, the selected option is used and the currently-typed text is ignored.
              - When selecting an entry with the keyboard and then the mouse selects an entry, the selection is lost.
            */
          setKeyboardSelected(reason === 'keyboard');
        }}
        onKeyDownCapture={(e) => {
          if (e.key === 'Enter' && !keyboardSelected) {
            (e.target as HTMLDivElement)?.blur(); // close the dropdown
            void doSearch();
          }
        }}
        onInputChange={(e, newValue) => {
          setSearchText(newValue);
        }}
        renderOption={renderOption}
        renderInput={(params) => (
          <TextField
            {...params}
            id="search-textinput"
            data-testid="search-textinput"
            name="search"
            variant="standard"
            autoFocus
            placeholder={'Search by topic title, diagnosis, symptom, lab, image or procedure'}
            InputProps={{
              ...params?.InputProps,
              startAdornment: (
                <IconButton id="search-icon" aria-label="search icon">
                  <SearchIcon />

                  {isSearching && <CircularProgress size={30} className={styles.progress} />}
                </IconButton>
              ),
            }}
            className={styles.searchInputField}
            onFocus={() => dispatch(closeFeedback())}
          />
        )}
      />
    </form>
  );
}
